import React, { useState } from "react";
import { MessageCircle, Mail, X, Headset } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { buildWhatsAppLink, buildEmailLink, defaultEnquiryMessage } from "@/lib/contact";
import { trackEvent } from "@/lib/analytics";

/* Floating direct-messaging widget (bottom-left to avoid the AI concierge FAB).
 * Expands to reveal WhatsApp and Email options with prefilled context. */
export const ContactWidget = () => {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const c = settings?.contact || {};

  const waLink = buildWhatsAppLink(c.whatsapp_number, defaultEnquiryMessage());
  const mailLink = buildEmailLink(c.email, "Villa enquiry", defaultEnquiryMessage());

  const channels = [
    waLink && { key: "whatsapp", label: "WhatsApp", href: waLink, icon: MessageCircle, color: "#25D366" },
    mailLink && { key: "email", label: "Email", href: mailLink, icon: Mail, color: "var(--lux-seaglass)" },
  ].filter(Boolean);

  if (channels.length === 0) return null;

  return (
    <div data-testid="contact-widget" className="fixed bottom-5 left-5 z-[120] flex flex-col items-start gap-3">
      <div
        className={`flex flex-col items-start gap-2 transition-all duration-200 ${
          open ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0"
        }`}
      >
        {channels.map((ch) => (
          <a
            key={ch.key}
            href={ch.href}
            target="_blank"
            rel="noreferrer"
            data-testid={`contact-${ch.key}-link`}
            onClick={() => trackEvent("contact_channel_click", { channel: ch.key })}
            className="group flex items-center gap-3 rounded-full border border-border bg-white/95 py-2 pl-2 pr-4 shadow-[var(--shadow-soft)] backdrop-blur-md transition-transform duration-200 hover:-translate-y-0.5"
          >
            <span
              className="flex h-9 w-9 items-center justify-center rounded-full text-white"
              style={{ backgroundColor: ch.color }}
            >
              <ch.icon className="h-4 w-4" />
            </span>
            <span className="text-sm font-medium text-[color:var(--lux-ink)]">{ch.label}</span>
          </a>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid="contact-widget-toggle"
        aria-label={open ? "Close contact options" : "Open contact options"}
        aria-expanded={open}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--lux-seaglass)] text-white shadow-[var(--shadow-glass)] transition-transform duration-200 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--lux-seaglass)] focus-visible:ring-offset-2"
    >
      {open ? <X className="h-5 w-5" /> : <Headset className="h-5 w-5" />}
    </button>
    </div>
  );
};

export default ContactWidget;
